<template>
  <div
    class="nav-item-container"
    :class="{ 'is-hidden-mobile': item.hiddenInMobile }"
    :data-e2e="`${NavCypressAttribute.buttons.NAV_ITEM}-${item.key}`"
  >
    <div v-if="isHeader" class="header">
      {{ item.title }}
    </div>
    <div v-else>
      <div class="nav-item">
        <TooltipView
          :active="item.isLocked && shouldShowTooltip"
          :position="TooltipPosition.IS_RIGHT"
          :type="TooltipType.IS_BLACK"
          class="is-full-width"
          :data-e2e="NavCypressAttribute.views.NAV_ITEM_TOOLTIP"
        >
          <template #content>
            <div
              class="is-flex is-flex-direction-row is-align-items-center is-full-width pl-3 p-2 is-size-5"
            >
              <span class="is-size-4">
                <i :class="iconClass" />
              </span>
              <p class="text whitespace-normal mx-2">
                {{ title }}
              </p>
              <div class="ml-auto is-flex is-justify-content-between is-align-items-center">
                <img
                  v-if="itemHasCustomBadge"
                  :src="getCustomBadgeSrc(item)"
                  :data-e2e="NavCypressAttribute.views.CUSTOM_BADGE"
                />
                <i
                  v-if="item.isLocked"
                  class="cls cls-lock-1 has-text-white ml-2"
                  :data-e2e="NavCypressAttribute.views.LOCK_ICON"
                />
              </div>
              <div class="ml-auto m-r-10 has-text-weight-bold" v-if="itemHasChild">
                <i class="cls cls-caret-right" :class="{ expanded: itemShow }"></i>
              </div>
            </div>
          </template>

          <component :is="routerComponent" :to="{ name: item.link }">
            <div
              @click.prevent="onClickNavItem()"
              class="is-flex nav-item-component is-align-items-center is-full-width"
              :data-e2e="NavCypressAttribute.views.NAV_ITEM"
              :data-guide="item.dataGuideKey"
            >
              <span class="icon is-size-2-mobile">
                <i :class="iconClass" />
              </span>
              <div class="is-flex is-flex-direction-row is-align-items-center is-full-width">
                <p class="text whitespace-normal is-hidden-tablet-only is-full-width m-0-mobile">
                  {{ title }}
                </p>
                <slot name="badge">
                  <TagView
                    v-if="itemHasBadge"
                    :type="item.badge.type"
                    class="m-l-2 is-radius-large"
                  >
                    {{ item.badge.text }}
                  </TagView>
                </slot>

                <div class="ml-auto is-flex is-justify-content-between is-align-items-center">
                  <img
                    v-if="itemHasCustomBadge"
                    class="custom-badge"
                    :src="getCustomBadgeSrc(item)"
                    :data-e2e="NavCypressAttribute.views.CUSTOM_BADGE"
                  />
                  <i
                    v-if="item.isLocked"
                    class="cls cls-lock-1 has-text-grey is-locked"
                    :data-e2e="NavCypressAttribute.views.LOCK_ICON"
                  />
                </div>
              </div>
              <div class="ml-auto m-r-10 has-text-weight-bold" v-if="itemHasChild">
                <i class="cls cls-caret-right is-hidden-mobile" :class="{ expanded: itemShow }"></i>
              </div>
            </div>
          </component>
        </TooltipView>
      </div>
      <div v-if="itemHasChild" :data-e2e="NavCypressAttribute.views.CHILDREN">
        <div v-show="canShowItem" class="child" :class="`level-${level + 1}`">
          <template v-for="subItem in item.children" :key="subItem.key">
            <component
              v-if="subItem.customComponent"
              :is="subItem.customComponent"
              :item="subItem"
              :level="level + 1"
            />
            <NavItem v-else :item="subItem" :level="level + 1" />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DeviceService from '/@/modules/core/ui/services/DeviceService';
import { NavItem as NavItemClass } from '/@/modules/core/nav/models/NavItem';
import { NavCypressAttribute } from '/@/modules/core/nav/constants/NavCypressAttribute';
import NavTrackingService from '/@/modules/core/nav/services/NavTrackingService';
import TagView from '@/modules/core/ui/components/wrappers/TagView.vue';
import TooltipView from '@/modules/core/ui/components/wrappers/TooltipView.vue';
import { TooltipPosition } from '@/modules/core/app/enums/tooltip/TooltipPosition';
import { TooltipType } from '@/modules/core/app/enums/tooltip/TooltipType';
import { DeviceBreakpoint } from '@/modules/core/ui/enums/DeviceBreakpoint';
import { HasResizeHandlerMixin } from '@/modules/core/app/mixins/HasResizeHandlerMixin';
import ApiService from '@/modules/core/api/services/ApiService';
import { Route } from '@/modules/core/app/constants/api.constants';
import NavItemBadgeFactory from '@/modules/core/nav/factories/NavItemBadgeFactory';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'NavItem',
  slotsDefinition: {
    badge: {}
  },
  mixins: [HasResizeHandlerMixin],
  components: { TagView, TooltipView },
  props: {
    item: { type: NavItemClass, required: true },
    level: {
      type: Number,
      default: 1
    },
    customClickFn: {
      type: Function,
      required: false
    }
  },
  static: {
    NavCypressAttribute,
    TooltipPosition,
    TooltipType
  },
  data() {
    return {
      itemShow: this.checkRouteMatch(this.item, this.$route.matched)
    };
  },
  computed: {
    title() {
      return DeviceService.isMobile() ? this.item.compactTitle : this.item.title;
    },
    shouldShowTooltip() {
      return this.windowWidth <= DeviceBreakpoint.DESKTOP;
    },
    isHeader() {
      return !!this.item.attributes?.header;
    },
    isFirstLevel() {
      return this.level === 1;
    },
    itemHasChild() {
      return !!this.item.children?.length;
    },
    itemHasBadge() {
      return !!this.item.badge?.text;
    },
    itemHasCustomBadge() {
      return !!this.item.customBadge;
    },
    iconClass() {
      return `cls cls-${this.item.icon}`;
    },
    routerComponent() {
      return this.item.link && !this.itemHasChild ? 'router-link' : 'a';
    },
    canShowItem: {
      get() {
        if (!this.itemHasChild) {
          return false;
        }

        return this.itemShow;
      },
      set(show) {
        this.itemShow = show;
      }
    }
  },
  methods: {
    checkRouteMatch(navItem, matchedRoutes) {
      if (matchedRoutes.some((route) => route.name === navItem.link)) {
        return true;
      }

      if (navItem.hasChildren) {
        return navItem.children.some((child) => this.checkRouteMatch(child, matchedRoutes));
      }

      return false;
    },
    onClickNavItem() {
      if (this.customClickFn) {
        return this.customClickFn();
      }

      if (this.$route.name === this.item.link && !this.itemHasChild) {
        return;
      }

      if (!this.itemHasChild) {
        ApiService.cancelPendingGetRequestsExceptIn([Route.AUTH, Route.NOTIFICATIONS]);
        this.$router.push({ name: this.item.link });
        NavTrackingService.track(this.item);
        return;
      }
      if (this.item.attributes.disabled) {
        return;
      }
      this.canShowItem = !this.canShowItem;
    },
    getCustomBadgeSrc(item) {
      return NavItemBadgeFactory.getCustomBadgeSrc(item);
    }
  }
};
</script>

<style scoped>
@media (min-width: CE_WIDESCREEN) {
  .is-locked {
    margin-left: 0.5rem;
  }
}

@media (max-width: CE_WIDESCREEN) {
  div:has(> .cls.cls-caret-right) {
    margin-right: var(--ce-2px) !important;
  }

  .custom-badge {
    width: 25%;
    top: var(--ce-5px);
    right: var(--ce-25px);
    position: absolute;
  }
}

:deep(.tooltip-content) {
  width: max-content;
}

.header {
  font-size: var(--ce-14px);
  font-weight: 600;
  padding: var(--ce-10px);
  white-space: nowrap;
  text-transform: uppercase;
  color: var(--ce-secondary);
}

div.nav-item {
  position: relative;

  &:hover {
    span.icon {
      color: var(--ce-primary);
    }

    a {
      color: var(--ce-primary);
      border-color: var(--ce-primary);
      border-left-color: transparent;
    }
  }

  span.icon {
    font-size: var(--ce-18px);
    color: var(--ce-secondary-light-light-10);
    padding-left: var(--ce-5px);
  }

  a {
    color: var(--ce-secondary);
    font-size: var(--ce-15px);
    height: var(--ce-52px);
    line-height: var(--ce-48px);
    padding-left: var(--ce-10px);
    border-radius: 0 10px 10px 0;
    background-color: transparent;
    border: 2px solid transparent;
    margin-top: var(--ce--2px);
    margin-right: var(--ce-10px);
    display: flex;
    align-items: center;

    p {
      display: block;
    }

    &.router-link-active {
      span.icon {
        color: var(--ce-primary);
      }

      font-weight: var(--ce-weight-medium);
      color: var(--ce-primary);
      margin-right: var(--ce-10px);
      background-color: var(--ce-primary-light-37);
      border-color: var(--ce-primary);
      border-left-color: transparent;
    }
  }

  .text {
    line-height: var(--ce-21px);
    margin: 0 var(--ce-8px);
  }

  .cls-caret-right {
    color: var(--ce-primary);
    font-size: var(--ce-12px);
    transition: transform 0.3s ease;

    &.expanded {
      transform: rotate(90deg);
    }
  }
}

div.nav-item :deep(.tag) {
  position: relative;
  top: 0;
  height: var(--ce-15px);
}

div.nav-item :deep(.tag span) {
  position: relative;
  top: 0;
}

.child.level-2 {
  div.nav-item {
    span.icon {
      font-size: var(--ce-13px);
    }

    a {
      font-size: var(--ce-13px);
      padding-left: var(--ce-15px);
      height: var(--ce-46px);
      line-height: var(--ce-40px);

      span {
        margin-left: var(--ce-5px);
      }
    }
  }
}
</style>
